import { Dimmer, Loader, Modal } from '@mairistem/composants';
import { getErrorMessage, type UserRole } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type TypePeriode from '../../../../constants/simulation/typePeriode';
import type TreeRow from '../../../../interfaces/feuilleSaisie/treeRow';
import type MontantSimulation from '../../../../interfaces/simulation/montantSimulation';
import type Simulation from '../../../../interfaces/simulation/simulation';
import { getMontantsByImputation } from '../../../../services/simulation/montantSimulation';
import { getImputationText } from '../../../../utils/imputation';
import MontantsSimulationModalContent from './MontantsSimulationModalContent';

interface MontantsSimulationModalProps {
	onAddMontant: () => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
	) => void;
	onClose: () => void;
	open: boolean;
	rowData: TreeRow;
	simulation: Simulation;
	userRole: UserRole;
}

const MontantsSimulationModal = ({
	onAddMontant,
	onChangeCommentaire,
	onChangeMontant,
	onClose,
	open,
	rowData,
	simulation,
	userRole,
}: MontantsSimulationModalProps) => {
	const [modalLoading, setModalLoading] = React.useState(true);
	const [montantsSimulation, setMontantsSimulation] = React.useState<
		MontantSimulation[]
	>([]);

	const fetchMontantsSimulation = async () => {
		try {
			const { data } = await getMontantsByImputation(
				new URLSearchParams({
					identifiantSimulation: simulation.identifiant.toString(),
					...(!isNil(rowData.idcm_analytique)
						? { identifiantAnalytique: rowData.idcm_analytique.toString() }
						: {}),
					...(!isNil(rowData.idcm_ventilation)
						? { identifiantVentilation: rowData.idcm_ventilation.toString() }
						: {}),
					...(!isNil(rowData.idcm_fonction)
						? { identifiantFonction: rowData.idcm_fonction.toString() }
						: {}),
					...(!isNil(rowData.idcm_operation)
						? { identifiantOperation: rowData.idcm_operation.toString() }
						: {}),
					identifiantChapitre: rowData.idcm_chapitre?.toString(),
					identifiantArticle: rowData.idcm_article?.toString(),
					sens: rowData.imp_sens,
					withEtiquettes: 'true',
				}),
			);
			setMontantsSimulation(data);
		} catch (e) {
			toast.error(
				getErrorMessage(
					e,
					'Erreur lors du chargement des montants de simulation',
				),
			);
		} finally {
			setModalLoading(false);
		}
	};

	return (
		<Modal
			closeIcon
			onClose={onClose}
			onMount={fetchMontantsSimulation}
			open={open}
			size="fullscreen"
		>
			<Modal.Header
				content={`Détail des demandes pour la ligne budgétaire ${getImputationText(rowData)}`}
			/>
			<Modal.Content>
				{modalLoading ? (
					<Dimmer active inverted>
						<Loader active />
					</Dimmer>
				) : (
					<MontantsSimulationModalContent
						identifiantEntite={simulation.exercice.identifiantEntite}
						identifiantSimulation={simulation.identifiant}
						montantsSimulation={montantsSimulation}
						onAddMontant={onAddMontant}
						onChangeCommentaire={onChangeCommentaire}
						onChangeMontant={onChangeMontant}
						rowData={rowData}
						setMontantsSimulation={setMontantsSimulation}
						transfertCpta={simulation.transfertCpta}
						typePeriode={simulation.typePeriode}
						userRole={userRole}
					/>
				)}
			</Modal.Content>
		</Modal>
	);
};

export default MontantsSimulationModal;
